import { useEffect, useRef } from 'react';
import { CANVAS_RESTORE_SNAPSHOT_EVENT } from 'agentable-canvas/whiteboard';
import { useCanvasSession } from '../contexts/CanvasSessionContext';
import {
  clearCanvasRestoreResolved,
  markCanvasRestoreResolved,
} from '../lib/canvas-restore-events';
import { getSiteCanvasState } from '../lib/site-media-api';

/**
 * Fetches persisted tldraw snapshot for the active site and dispatches
 * `landi:canvas-restore-snapshot` for agentable-canvas to load (Stage 10).
 */
export function useCanvasStatePersistence(): void {
  const { session } = useCanvasSession();
  const siteId = session.siteId;
  const restoreGenerationRef = useRef(0);

  useEffect(() => {
    if (!siteId) return;

    clearCanvasRestoreResolved(siteId);
    const generation = ++restoreGenerationRef.current;

    void (async () => {
      try {
        const state = await getSiteCanvasState(siteId);
        if (generation !== restoreGenerationRef.current) {
          return;
        }
        if (!state?.snapshot) {
          markCanvasRestoreResolved(siteId, false);
          return;
        }
        window.dispatchEvent(
          new CustomEvent(CANVAS_RESTORE_SNAPSHOT_EVENT, {
            detail: { snapshot: state.snapshot, scopeKey: siteId },
            bubbles: true,
            composed: true,
          }),
        );
        markCanvasRestoreResolved(siteId, true);
      } catch (err: unknown) {
        console.error('[useCanvasStatePersistence] restore failed', err);
        if (generation === restoreGenerationRef.current) {
          markCanvasRestoreResolved(siteId, false);
        }
      }
    })();
  }, [siteId]);
}
